import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  approvePurchasePlan,
  batchApprovePurchasePlan,
  cancelPurchasePlan,
  deletePurchasePlan,
  downloadPurchaseTemplate,
  exportPurchasePlanDetail,
  getPurchasePlanPages,
  submitPurchasePlan,
  unapprovePurchasePlan,
  type PurchasePlanRecord,
} from '@/api/purchase';
import { getAuthDepartments } from '@/api/auth';
import Pagination from '@/components/base/Pagination';
import MonthPicker, { addMonthsValue, currentMonthValue, nextMonthValue } from '@/components/base/MonthPicker';
import SearchableSelect from '@/components/base/SearchableSelect';
import { useMember } from '@/hooks/useMember';
import { getUser, isDivideAccount, isPurchasePlanManager } from '@/utils/auth';
import type { ApprovalStep } from '@/types/order';
import ApproveModal from './components/ApproveModal';
import CancelModal from './components/CancelModal';
import ConfirmModal from './components/ConfirmModal';
import DownloadTemplateModal, {
  type DownloadTemplateValues,
} from './components/DownloadTemplateModal';

const PAGE_SIZE = 20;

const STATUS_FILTERS = [
  { id: 'all', label: '全部', value: undefined as number | undefined },
  { id: 'todo', label: '我的待办', value: undefined as number | undefined },
  { id: '0', label: '未处理', value: 0 },
  { id: '1', label: '审批中', value: 1 },
  { id: '10', label: '已通过', value: 10 },
  { id: '2', label: '已拒绝', value: 2 },
  { id: '99', label: '已取消', value: 99 },
];

type DotTone = 'done' | 'rejected' | 'current' | 'pending';

interface ApprovalNode {
  id: string;
  title: string;
  subtitle?: string;
  statusText?: string;
  statusTone?: DotTone;
  time?: string;
  comment?: string;
  dot: DotTone;
}

function mapApproveState(state: number | string | undefined): ApprovalStep['status'] {
  const n = Number(state);
  if (n === 1) return 'approved';
  if (n === 2) return 'rejected';
  return 'pending';
}

function mapInstanToSteps(instan: PurchasePlanRecord['instan']): ApprovalStep[] {
  if (!Array.isArray(instan) || instan.length === 0) return [];

  return instan
    .filter((flow) => {
      const sort = Number(flow.sort ?? 0);
      const nowSort = Number(flow.now_step_sort ?? 0);
      const isOver = Number(flow.is_over ?? 0);
      // 无 now_step_sort 时保留全部节点
      if (!flow.now_step_sort && flow.now_step_sort !== 0) return true;
      return sort <= nowSort || isOver !== 1;
    })
    .map((flow, index) => {
      const status = mapApproveState(flow.approve_state);
      const stepName = String(flow.step_name || flow.step || `第${index + 1}级审批`);
      const approverName =
        status === 'pending'
          ? String(flow.account_names || flow.account_name || '待定')
          : String(flow.account_name || flow.account_names || '');

      return {
        id: String(flow.id ?? `flow-${index}`),
        level: Number(flow.sort ?? index + 1) || index + 1,
        approverName,
        role: stepName,
        status,
        approvedAt: flow.approve_time ? String(flow.approve_time) : undefined,
        comment: flow.approve ? String(flow.approve) : undefined,
      };
    });
}

function getPurchaseStatusTag(status: number | string | undefined, statusText?: string): {
  color: string;
  label: string;
  icon: string;
} {
  const n = Number(status);
  const label = statusText || '-';
  if (n === 10) return { color: '#19be6b', label, icon: 'ri-checkbox-circle-line' };
  if (n === 1) return { color: '#2d8cf0', label, icon: 'ri-loader-4-line' };
  if (n === 2) return { color: '#ed4014', label, icon: 'ri-close-circle-line' };
  if (n === 99) return { color: '#c5c8ce', label, icon: 'ri-close-line' };
  return { color: '#33bcb7', label, icon: 'ri-time-line' };
}

function buildPurchaseApprovalNodes(row: PurchasePlanRecord): ApprovalNode[] {
  const status = Number(row.status);
  const inApproval = status === 0 || status === 1;
  const isRejected = status === 2;
  const isCancelled = status === 99;
  const steps = mapInstanToSteps(row.instan);
  const submitter = row.member?.nickname || row.member?.account || undefined;

  const nodes: ApprovalNode[] = [
    {
      id: 'submit',
      title: '提交采购计划',
      subtitle: submitter,
      time: row.created_at,
      dot: 'done',
    },
  ];

  if (isCancelled) {
    nodes.push({
      id: 'cancelled',
      title: '计划已取消',
      subtitle: submitter,
      statusText: '已取消',
      statusTone: 'rejected',
      dot: 'rejected',
    });
    return nodes;
  }

  if (steps.length > 0) {
    let currentMarked = false;
    steps.forEach((step) => {
      let dot: DotTone;
      let statusText: string;
      let subtitle: string | undefined;

      if (step.status === 'approved') {
        dot = 'done';
        statusText = '已审批';
        subtitle = step.approverName ? `${step.approverName}（同意）` : undefined;
      } else if (step.status === 'rejected') {
        dot = 'rejected';
        statusText = '已拒绝';
        subtitle = step.approverName ? `${step.approverName}（拒绝）` : undefined;
      } else if (!currentMarked && inApproval) {
        dot = 'current';
        statusText = '待审批';
        subtitle = step.approverName ? `等待 ${step.approverName} 审批` : undefined;
        currentMarked = true;
      } else {
        dot = 'pending';
        statusText = '待审批';
        subtitle = step.approverName ? `等待 ${step.approverName} 审批` : undefined;
      }

      nodes.push({
        id: step.id || `appr-${step.level}`,
        title: step.role,
        subtitle,
        statusText,
        statusTone: dot,
        time: step.approvedAt,
        comment: step.comment,
        dot,
      });
    });
  } else if (!isRejected) {
    nodes.push({
      id: 'approved',
      title: status === 10 ? '审批通过' : '待审批',
      time: undefined,
      statusText: status === 10 ? '已通过' : '待审批',
      statusTone: status === 10 ? 'done' : inApproval ? 'current' : 'pending',
      dot: status === 10 ? 'done' : inApproval ? 'current' : 'pending',
    });
  }

  return nodes;
}

function getRejectReason(row: PurchasePlanRecord): string | undefined {
  const rejected = (row.instan || []).find((f) => Number(f.approve_state) === 2);
  const reason = rejected?.approve ? String(rejected.approve).trim() : '';
  return reason || undefined;
}

/** 当前审批节点 account_ids 是否包含用户 */
function accountIdsInclude(
  accountIds: string | null | undefined,
  userId: string | number | null | undefined,
) {
  if (userId == null || userId === '') return false;
  const uid = String(userId).trim();
  if (!uid) return false;
  return String(accountIds || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .some((id) => id === uid);
}

/** 取当前审批节点：sort === now_step_sort；无 now_step_sort 时取首个待审节点 */
function getCurrentApproveStep(row: PurchasePlanRecord) {
  const instan = row.instan;
  if (!Array.isArray(instan) || instan.length === 0) return null;
  const bySort = instan.find((f) => Number(f.sort) === Number(f.now_step_sort));
  if (bySort) return bySort;
  return instan.find((f) => Number(f.approve_state) === 0) || null;
}

/** 当前账号是否属于当前审批节点审批人 */
function resolveCanApprove(row: PurchasePlanRecord, userId?: string | number | null) {
  const status = Number(row.status);
  if (status !== 0 && status !== 1) return false;
  const current = getCurrentApproveStep(row);
  if (!current || Number(current.approve_state) !== 0) return false;
  return accountIdsInclude(current.account_ids, userId ?? getUser()?.id);
}

/** 未审批（未处理/审批中）可取消 */
function resolveCanCancel(row: PurchasePlanRecord) {
  const status = Number(row.status);
  return status === 0 || status === 1;
}

/** 已取消可删除；采购计划管理员在可编辑状态（未处理/审批中）也可删除 */
function resolveCanDelete(row: PurchasePlanRecord) {
  const status = Number(row.status);
  if (status === 99) return true;
  if ((status === 0 || status === 1) && isPurchasePlanManager()) return true;
  return false;
}

/**
 * 反审权限：须已通过，且当前账号属于最近一次已通过步骤的审批人
 *（与后端 FlowInstanceService::reverse 一致）
 */
function resolveCanReverse(row: PurchasePlanRecord, userId?: string | number | null) {
  if (Number(row.status) !== 10) return false;
  const instan = Array.isArray(row.instan) ? row.instan : [];
  const lastPassed = [...instan]
    .filter((f) => Number(f.approve_state) === 1)
    .sort((a, b) => Number(b.sort ?? 0) - Number(a.sort ?? 0))[0];
  if (!lastPassed) return false;
  return accountIdsInclude(lastPassed.account_ids, userId ?? getUser()?.id);
}

/** 审批中：本科室提交人或任一审批人可覆盖上传清单 */
function resolveCanEdit(row: PurchasePlanRecord, userId?: string | number | null) {
  const status = Number(row.status);
  if (status !== 0 && status !== 1) return false;

  const user = getUser();
  const uid = userId ?? user?.id;
  if (uid == null || uid === '') return false;

  // 普通账号须同一科室；is_divide 可代管范围内科室
  if (!isDivideAccount()) {
    const userDept = user?.department_id ?? user?.department?.id;
    const rowDept = row.office?.id;
    if (userDept != null && rowDept != null && String(userDept) !== String(rowDept)) {
      return false;
    }
  }

  // 提交人
  if (row.account_id != null && String(row.account_id) === String(uid)) {
    return true;
  }

  // 任一审批节点审批人
  const instan = row.instan || [];
  return instan.some((step) => accountIdsInclude(step.account_ids, uid));
}

function applyMonthToValue(month?: string | number) {
  const s = String(month || '').trim();
  if (/^\d{6}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
  if (/^\d{4}-\d{2}$/.test(s)) return s;
  return '';
}

function formatTime(value?: string) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface UploadErrors {
  month?: string;
  department?: string;
  file?: string;
}

export default function PurchasePlanPage() {
  const navigate = useNavigate();
  /** 仅可提交下月起连续 3 个月，不可选本月或更早 */
  const minSubmitMonth = useMemo(() => nextMonthValue(), []);
  const maxSubmitMonth = useMemo(() => addMonthsValue(new Date(), 3), []);
  const [filterMonth, setFilterMonth] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { purchasePlansCount: todoCount, refresh: refreshMember } = useMember();
  const [selectedDepts, setSelectedDepts] = useState<string[]>([]);
  const [deptOptions, setDeptOptions] = useState<string[]>([]);
  const [deptIdMap, setDeptIdMap] = useState<Record<string, string>>({});
  const deptIdMapRef = useRef<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [list, setList] = useState<PurchasePlanRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [openApprovalId, setOpenApprovalId] = useState<string | null>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const popupRef = useRef<HTMLDivElement | null>(null);

  const [downloading, setDownloading] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadMonth, setUploadMonth] = useState('');
  const [uploadDeptTitle, setUploadDeptTitle] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadRemark, setUploadRemark] = useState('');
  const [uploadErrors, setUploadErrors] = useState<UploadErrors>({});
  const [uploadEditFlowId, setUploadEditFlowId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [modal, setModal] = useState<
    | { type: 'approve'; flowId: string }
    | { type: 'batch-approve' }
    | { type: 'cancel'; flowId: string }
    | { type: 'reverse'; flowId: string }
    | { type: 'delete'; flowId: string }
    | null
  >(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const showDeptFilter = isDivideAccount();

  const refreshTodoCount = useCallback(async () => {
    await refreshMember({ force: true });
  }, [refreshMember]);

  useEffect(() => {
    deptIdMapRef.current = deptIdMap;
  }, [deptIdMap]);

  useEffect(() => {
    if (!showDeptFilter) {
      setDeptOptions([]);
      setDeptIdMap({});
      setSelectedDepts([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const res = await getAuthDepartments();
        const ok = res.error === false || Number(res.error) === 0;
        if (cancelled || !ok || !Array.isArray(res.data)) return;

        const nextMap: Record<string, string> = {};
        const nextOptions: string[] = [];
        res.data.forEach((item) => {
          const id = Number(item.id);
          const title = String(item.title || '').trim();
          if (!title || !Number.isFinite(id) || id <= 0) return;
          if (!nextMap[title]) {
            nextMap[title] = String(id);
            nextOptions.push(title);
          }
        });
        setDeptIdMap(nextMap);
        setDeptOptions(nextOptions);
      } catch {
        if (!cancelled) {
          setDeptOptions([]);
          setDeptIdMap({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showDeptFilter]);

  const fetchList = useCallback(async (pageNum: number) => {
    setLoading(true);
    setLoadError('');
    try {
      const isTodo = statusFilter === 'todo';
      const statusItem = STATUS_FILTERS.find((f) => f.id === statusFilter);
      const year = filterMonth ? Number(filterMonth.slice(0, 4)) : new Date().getFullYear();
      const idMap = deptIdMapRef.current;
      const departmentIds =
        showDeptFilter && selectedDepts.length > 0
          ? selectedDepts
              .map((title) => Number(idMap[title]))
              .filter((id) => Number.isFinite(id) && id > 0)
          : undefined;
      const res = await getPurchasePlanPages({
        page: pageNum,
        pagenumber: PAGE_SIZE,
        year,
        month: filterMonth || undefined,
        status: !isTodo ? statusItem?.value : undefined,
        department_ids: departmentIds,
        is_todo: isTodo || undefined,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setList([]);
        setTotal(0);
        setLoadError(res.message || res.msg || '采购计划加载失败');
        return;
      }
      setList(Array.isArray(res.data) ? res.data : []);
      setTotal(Number(res.total ?? 0) || 0);
    } catch {
      setList([]);
      setTotal(0);
      setLoadError('采购计划加载失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [filterMonth, statusFilter, selectedDepts, showDeptFilter]);

  useEffect(() => {
    void fetchList(page);
    setOpenApprovalId(null);
    setPopupPos(null);
    setSelectedIds([]);
  }, [page, fetchList]);

  useEffect(() => {
    setPage(1);
    setOpenApprovalId(null);
    setPopupPos(null);
    setSelectedIds([]);
  }, [filterMonth, statusFilter, selectedDepts]);

  const handleDeptChange = (value: string | string[]) => {
    setSelectedDepts(Array.isArray(value) ? value : value ? [value] : []);
  };

  const toggleApproval = (rowId: string) => {
    if (openApprovalId === rowId) {
      setOpenApprovalId(null);
      setPopupPos(null);
      return;
    }
    const trigger = triggerRefs.current[rowId];
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      const popupWidth = 288;
      let left = rect.left;
      if (left + popupWidth > window.innerWidth - 16) {
        left = window.innerWidth - popupWidth - 16;
      }
      if (left < 12) left = 12;
      setPopupPos({
        top: rect.bottom + 4,
        left,
      });
    }
    setOpenApprovalId(rowId);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const insidePopup = popupRef.current?.contains(target);
      const insideTrigger = openApprovalId
        ? triggerRefs.current[openApprovalId]?.contains(target)
        : false;
      if (!insidePopup && !insideTrigger) {
        setOpenApprovalId(null);
        setPopupPos(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openApprovalId]);

  useEffect(() => {
    const handleClose = () => {
      setOpenApprovalId(null);
      setPopupPos(null);
    };
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);
    return () => {
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, []);

  const openRow = openApprovalId
    ? list.find((row) => String(row.id) === openApprovalId) ?? null
    : null;
  const openApprovalNodes = openRow ? buildPurchaseApprovalNodes(openRow) : [];
  const openRejectReason = openRow ? getRejectReason(openRow) : undefined;

  const modalRow =
    modal && 'flowId' in modal
      ? list.find((row) => String(row.id) === modal.flowId) ?? null
      : null;

  const showBatchSelect = isPurchasePlanManager();
  const selectablePlans = useMemo(
    () => (showBatchSelect ? list.filter((row) => resolveCanApprove(row)) : []),
    [list, showBatchSelect],
  );
  const selectedPlans = selectablePlans.filter((row) => selectedIds.includes(String(row.id)));
  const allSelectableSelected =
    selectablePlans.length > 0 &&
    selectablePlans.every((row) => selectedIds.includes(String(row.id)));
  const someSelectableSelected = selectedIds.length > 0 && !allSelectableSelected;

  const toggleSelectPlan = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const toggleSelectAllPlans = () => {
    if (allSelectableSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(selectablePlans.map((row) => String(row.id)));
  };

  const handleApproveSubmit = async (isAgree: boolean, remark: string) => {
    if (!modal || modal.type !== 'approve') return;
    setActionSubmitting(true);
    try {
      const res = await approvePurchasePlan({
        flow_id: modal.flowId,
        is_agree: isAgree,
        remark,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '审批失败', 'error');
        return;
      }
      showToast(isAgree ? '已同意审批' : '已拒绝审批');
      setModal(null);
      setSelectedIds((prev) => prev.filter((id) => id !== String(modal.flowId)));
      void fetchList(page);
      void refreshTodoCount();
    } catch {
      showToast('审批失败，请稍后重试', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleBatchApproveSubmit = async (isAgree: boolean, remark: string) => {
    if (!modal || modal.type !== 'batch-approve') return;
    if (selectedIds.length === 0) {
      showToast('请先选择需要审批的采购计划', 'error');
      return;
    }
    setActionSubmitting(true);
    try {
      const res = await batchApprovePurchasePlan({
        flow_ids: selectedIds.map((id) => Number(id) || id),
        is_agree: isAgree,
        remark,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '批量审批失败', 'error');
        return;
      }
      showToast(res.message || (isAgree ? '批量审批通过' : '批量审批已拒绝'));
      setModal(null);
      setSelectedIds([]);
      void fetchList(page);
      void refreshTodoCount();
    } catch {
      showToast('批量审批失败，请稍后重试', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleCancelSubmit = async (remark: string) => {
    if (!modal || modal.type !== 'cancel') return;
    setActionSubmitting(true);
    try {
      const res = await cancelPurchasePlan({
        flow_id: modal.flowId,
        remark,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '取消失败', 'error');
        return;
      }
      showToast(res.message || '采购计划已取消');
      setModal(null);
      void fetchList(page);
      void refreshTodoCount();
    } catch {
      showToast('取消失败，请稍后重试', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleReverseConfirm = async () => {
    if (!modal || modal.type !== 'reverse') return;
    setActionSubmitting(true);
    try {
      const res = await unapprovePurchasePlan({
        flow_id: modal.flowId,
        remark: '反审退回',
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '反审失败', 'error');
        return;
      }
      showToast(res.message || '采购计划反审成功');
      setModal(null);
      void fetchList(page);
      void refreshTodoCount();
    } catch {
      showToast('反审失败，请稍后重试', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!modal || modal.type !== 'delete') return;
    setActionSubmitting(true);
    try {
      const res = await deletePurchasePlan(modal.flowId);
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '删除失败', 'error');
        return;
      }
      showToast(res.message || '采购计划已删除');
      setModal(null);
      if (openApprovalId === modal.flowId) {
        setOpenApprovalId(null);
        setPopupPos(null);
      }
      void fetchList(page);
      void refreshTodoCount();
    } catch {
      showToast('删除失败，请稍后重试', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  const openDownloadModal = () => {
    if (downloading) return;
    setDownloadOpen(true);
  };

  const closeDownloadModal = () => {
    if (downloading) return;
    setDownloadOpen(false);
  };

  const handleDownloadTemplate = async (values: DownloadTemplateValues) => {
    if (downloading) return;

    let departmentId: string | undefined;
    if (showDeptFilter && values.departmentTitle) {
      departmentId =
        deptIdMapRef.current[values.departmentTitle] || deptIdMap[values.departmentTitle];
      if (!departmentId) {
        showToast('科室信息无效，请刷新后重试', 'error');
        return;
      }
    } else if (showDeptFilter && !isPurchasePlanManager()) {
      showToast('请选择科室', 'error');
      return;
    }

    const hasFrom = !!values.startTime;
    const hasTo = !!values.endTime;
    if (hasFrom !== hasTo) {
      showToast('请同时选择起始日期和结束日期', 'error');
      return;
    }
    if (hasFrom && hasTo && values.startTime > values.endTime) {
      showToast('结束日期不能早于起始日期', 'error');
      return;
    }

    setDownloading(true);
    try {
      const res = await downloadPurchaseTemplate({
        department_id: departmentId,
        start_time: hasFrom ? values.startTime : undefined,
        end_time: hasTo ? values.endTime : undefined,
      });
      const ok = res.error === false || Number(res.error) === 0;
      const fileUrl = String(res.url || '').trim();
      if (!ok || !fileUrl) {
        showToast(res.message || res.msg || '模板下载失败', 'error');
        return;
      }
      const fromUrl = decodeURIComponent(
        fileUrl.split('/').pop()?.split('?')[0] || '',
      );
      const filename =
        String(res.filename || '').trim() ||
        fromUrl ||
        '采购计划表模板.xlsx';
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadOpen(false);
      showToast(res.message || '模板已开始下载（计划数量已按所选统计周期预填）');
    } catch {
      showToast('模板下载失败，请稍后重试', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleExportPlan = async (row: PurchasePlanRecord) => {
    const flowId = String(row.id);
    if (!flowId || exportingId) return;
    setExportingId(flowId);
    try {
      const res = await exportPurchasePlanDetail({ flow_id: flowId });
      const ok = res.error === false || Number(res.error) === 0;
      const fileUrl = String(res.url || '').trim();
      if (!ok || !fileUrl) {
        showToast(res.message || res.msg || '导出失败', 'error');
        return;
      }
      const fromUrl = decodeURIComponent(fileUrl.split('/').pop()?.split('?')[0] || '');
      const filename =
        String(res.filename || '').trim() ||
        fromUrl ||
        '采购计划表.xlsx';
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast(res.message || '采购计划表已开始下载');
    } catch {
      showToast('导出失败，请稍后重试', 'error');
    } finally {
      setExportingId(null);
    }
  };

  const openUploadModal = () => {
    setUploadEditFlowId(null);
    setUploadMonth(minSubmitMonth);
    const defaultDept =
      selectedDepts.length === 1
        ? selectedDepts[0]
        : deptOptions.length === 1
          ? deptOptions[0]
          : '';
    setUploadDeptTitle(showDeptFilter ? defaultDept : '');
    setUploadFile(null);
    setUploadRemark('');
    setUploadErrors({});
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadOpen(true);
  };

  const openEditUploadModal = (row: PurchasePlanRecord) => {
    const month = applyMonthToValue(row.apply_month) || minSubmitMonth;
    setUploadEditFlowId(String(row.id));
    setUploadMonth(month);
    setUploadDeptTitle(String(row.office?.title || '').trim());
    setUploadFile(null);
    setUploadRemark('');
    setUploadErrors({});
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadOpen(true);
  };

  const closeUploadModal = () => {
    if (submitting) return;
    setUploadOpen(false);
    setUploadEditFlowId(null);
    setUploadDeptTitle('');
  };

  const validateUpload = (): UploadErrors => {
    const next: UploadErrors = {};
    const isEdit = !!uploadEditFlowId;
    if (!uploadMonth) {
      next.month = '请选择计划月份';
    } else if (
      !isEdit &&
      (uploadMonth < minSubmitMonth || uploadMonth > maxSubmitMonth)
    ) {
      next.month = `只能提交下月起共 3 个月的计划（${minSubmitMonth} ~ ${maxSubmitMonth}）`;
    }
    if (!uploadFile) {
      next.file = '请上传采购清单模板';
    } else {
      const name = uploadFile.name.toLowerCase();
      if (!/\.(xlsx|xls|csv)$/.test(name)) {
        next.file = '仅支持 xlsx / xls / csv 文件';
      }
    }
    return next;
  };

  const handleSubmitUpload = async () => {
    if (submitting) return;
    const errs = validateUpload();
    setUploadErrors(errs);
    if (Object.keys(errs).length > 0) {
      showToast(errs.department || errs.month || errs.file || '请完善表单', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const isEdit = !!uploadEditFlowId;
      const departmentId =
        showDeptFilter && uploadDeptTitle ? deptIdMap[uploadDeptTitle] : undefined;
      if (showDeptFilter && !isEdit && uploadDeptTitle && !departmentId) {
        showToast('请选择有效的采购计划科室', 'error');
        setSubmitting(false);
        return;
      }
      const res = await submitPurchasePlan({
        month: uploadMonth,
        file: uploadFile!,
        remark: uploadRemark.trim() || undefined,
        // 未选科室时不传，后端默认当前账号所在科室
        department_id: !isEdit && departmentId ? departmentId : undefined,
        flow_id: isEdit ? uploadEditFlowId! : undefined,
        mode: isEdit ? 'overwrite' : undefined,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(
          res.message || res.msg || (isEdit ? '采购计划修改失败' : '采购计划提交失败'),
          'error',
        );
        return;
      }
      showToast(res.message || (isEdit ? '采购计划清单已覆盖更新' : '采购计划提交成功'));
      setUploadOpen(false);
      setUploadEditFlowId(null);
      setUploadDeptTitle('');
      if (!isEdit) setPage(1);
      void fetchList(isEdit ? page : 1);
    } catch {
      showToast('网络异常，提交失败', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 bg-background-100">
      {toast && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[130] px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
            toast.type === 'success'
              ? 'bg-accent-500 text-background-50'
              : 'bg-red-500 text-white'
          }`}
        >
          <i
            className={
              toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            }
          />
          {toast.message}
        </div>
      )}

      <div className="px-4 md:px-6 pt-0 pb-4 md:pb-6">
        <div className="bg-white rounded-lg border border-background-200 p-4 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            {showDeptFilter && (
              <div className="min-w-[14rem] max-w-xs flex-1 basis-[14rem]">
                <SearchableSelect
                  multiple
                  options={deptOptions}
                  value={selectedDepts}
                  onChange={handleDeptChange}
                  placeholder="搜索 / 选择科室"
                  allLabel="全部科室"
                  icon="ri-hospital-line"
                  searchPlaceholder="继续搜索科室..."
                />
              </div>
            )}

            <div className="w-44 shrink-0">
              <MonthPicker
                value={filterMonth}
                onChange={setFilterMonth}
                placeholder="选择计划月份"
              />
            </div>

            <button
              type="button"
              onClick={() => navigate('/purchase-plan/create')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-xs font-medium transition-colors shrink-0"
            >
              <i className="ri-add-line" />
              创建采购计划
            </button>

            {isPurchasePlanManager() && (
              <button
                type="button"
                onClick={() => {
                  navigate(
                    `/purchase-plan/month-details?month=${encodeURIComponent(currentMonthValue())}`,
                  );
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300 bg-transparent text-foreground-700 text-xs font-medium hover:bg-background-50 hover:border-primary-300 hover:text-primary-600 transition-colors shrink-0 cursor-pointer"
              >
                <i className="ri-table-line" />
                月计划汇总
              </button>
            )}

            <div className="ml-auto flex items-center gap-2 shrink-0">
              <button
                type="button"
                disabled={downloading}
                onClick={openDownloadModal}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium transition-colors disabled:opacity-60"
              >
                <i className="ri-download-2-line" />
                下载计划表模板
              </button>
              <button
                type="button"
                onClick={openUploadModal}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium transition-colors"
              >
                <i className="ri-upload-2-line" />
                上传计划表
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <div className="px-5 pt-4 pb-3 border-b border-background-100 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 overflow-x-auto">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setStatusFilter(f.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border whitespace-nowrap shrink-0 transition-colors ${
                    statusFilter === f.id
                      ? 'bg-primary-500 text-background-50 border-primary-500'
                      : 'border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {f.label}
                  {f.id === 'todo' && todoCount > 0 && (
                    <span className="min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center font-medium">
                      {todoCount > 99 ? '99+' : todoCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {showBatchSelect && (
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  已选 <strong className="text-foreground-900">{selectedIds.length}</strong> 条
                </span>
                <button
                  type="button"
                  disabled={selectedIds.length === 0 || actionSubmitting}
                  onClick={() => setModal({ type: 'batch-approve' })}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs border border-primary-500 bg-primary-500 text-background-50 hover:bg-primary-600 hover:border-primary-600 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-checkbox-multiple-line" />
                  批量审批
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400" />
              <p className="text-sm text-foreground-500 mt-3">正在加载采购计划...</p>
            </div>
          ) : loadError ? (
            <div className="text-center py-16">
              <i className="ri-error-warning-line text-2xl text-primary-500" />
              <p className="text-sm text-foreground-500 mt-3 mb-3">{loadError}</p>
              <button
                type="button"
                onClick={() => void fetchList(page)}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                重新加载
              </button>
            </div>
          ) : list.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-calendar-check-line text-2xl text-foreground-300" />
              </div>
              <p className="text-sm text-foreground-500">暂无采购计划记录</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-50 border-b border-background-200">
                    {showBatchSelect && (
                      <th className="py-3 px-1 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap w-14">
                        <label
                          className={`inline-flex items-center justify-center w-10 h-10 mx-auto rounded-md transition-colors ${
                            selectablePlans.length === 0
                              ? 'cursor-not-allowed opacity-40'
                              : 'cursor-pointer hover:bg-background-100'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={allSelectableSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = someSelectableSelected;
                            }}
                            onChange={toggleSelectAllPlans}
                            disabled={selectablePlans.length === 0}
                            className="sr-only"
                            title="全选本页可审批计划"
                            aria-label="全选本页可审批计划"
                          />
                          <span
                            className={`inline-flex items-center justify-center w-5 h-5 rounded-md border-2 transition-colors ${
                              allSelectableSelected || someSelectableSelected
                                ? 'bg-primary-500 border-primary-500 text-white'
                                : 'bg-white border-foreground-300'
                            }`}
                            aria-hidden
                          >
                            {allSelectableSelected ? (
                              <i className="ri-check-line text-sm leading-none" />
                            ) : someSelectableSelected ? (
                              <i className="ri-subtract-line text-sm leading-none" />
                            ) : null}
                          </span>
                        </label>
                      </th>
                    )}
                    <th className="py-3 px-4 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      计划月份
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      科室
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      提交人
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      状态
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      采购总数
                    </th>
                    <th className="py-3 px-4 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      采购总额
                    </th>
                    <th className="py-3 px-4 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((row) => {
                    const id = String(row.id);
                    const canApprove = resolveCanApprove(row);
                    const isSelected = selectedIds.includes(id);
                    return (
                        <tr
                          key={id}
                          className={`border-b border-background-100 hover:bg-background-50/60 transition-colors ${
                            isSelected ? 'bg-primary-50/50' : ''
                          }`}
                        >
                          {showBatchSelect && (
                            <td className="py-2 px-1 text-center align-middle">
                              <label
                                className={`inline-flex items-center justify-center w-10 h-10 mx-auto rounded-md transition-colors ${
                                  canApprove
                                    ? 'cursor-pointer hover:bg-primary-50'
                                    : 'cursor-not-allowed opacity-40'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={!canApprove}
                                  onChange={() => toggleSelectPlan(id)}
                                  className="sr-only"
                                  aria-label={`选择采购计划 ${id}`}
                                />
                                <span
                                  className={`inline-flex items-center justify-center w-5 h-5 rounded-md border-2 transition-colors ${
                                    isSelected
                                      ? 'bg-primary-500 border-primary-500 text-white'
                                      : 'bg-white border-foreground-300'
                                  }`}
                                  aria-hidden
                                >
                                  {isSelected ? (
                                    <i className="ri-check-line text-sm leading-none" />
                                  ) : null}
                                </span>
                              </label>
                            </td>
                          )}
                          <td className="py-3 px-4 text-foreground-800 whitespace-nowrap">
                            {row.apply_month_text || row.apply_month || '-'}
                          </td>
                          <td className="py-3 px-4 text-foreground-700 whitespace-nowrap">
                            {row.office?.title || '-'}
                          </td>
                          <td className="py-3 px-4 text-foreground-700 whitespace-nowrap">
                            {row.member?.nickname || row.member?.account || '-'}
                          </td>
                          <td className="py-3 px-4">
                            {(() => {
                              const tag = getPurchaseStatusTag(row.status, row.status_text);
                              const isOpen = openApprovalId === id;
                              return (
                                <button
                                  ref={(el) => {
                                    triggerRefs.current[id] = el;
                                  }}
                                  type="button"
                                  onClick={() => toggleApproval(id)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium cursor-pointer transition-opacity whitespace-nowrap hover:opacity-85"
                                  style={{
                                    backgroundColor: tag.color,
                                    color: '#fff',
                                    border: `1px solid ${tag.color}`,
                                  }}
                                >
                                  <i className={`${tag.icon} text-xs`} />
                                  {tag.label}
                                  <i
                                    className={`text-[10px] transition-transform ${
                                      isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                                    }`}
                                  />
                                </button>
                              );
                            })()}
                          </td>
                          <td className="py-3 px-4 text-center text-foreground-800 whitespace-nowrap">
                            {Number(row.plan_quantity ?? 0) || 0}
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-primary-600 whitespace-nowrap">
                            ¥{Number(row.plan_amount ?? 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="inline-flex items-center justify-center gap-1.5 flex-wrap">
                              {canApprove && (
                                <button
                                  type="button"
                                  onClick={() => setModal({ type: 'approve', flowId: id })}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-primary-500 bg-primary-500 text-background-50 hover:bg-primary-600 hover:border-primary-600 transition-colors whitespace-nowrap"
                                >
                                  <i className="ri-shield-check-line text-xs" />
                                  审批
                                </button>
                              )}
                              {resolveCanReverse(row) && (
                                <button
                                  type="button"
                                  onClick={() => setModal({ type: 'reverse', flowId: id })}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400 transition-colors whitespace-nowrap"
                                >
                                  <i className="ri-arrow-go-back-line text-xs" />
                                  反审
                                </button>
                              )}
                              {resolveCanCancel(row) && (
                                <button
                                  type="button"
                                  onClick={() => setModal({ type: 'cancel', flowId: id })}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300 transition-colors whitespace-nowrap"
                                >
                                  <i className="ri-close-circle-line text-xs" />
                                  取消
                                </button>
                              )}
                              {resolveCanDelete(row) && (
                                <button
                                  type="button"
                                  onClick={() => setModal({ type: 'delete', flowId: id })}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border text-white transition-colors whitespace-nowrap hover:opacity-90"
                                  style={{ backgroundColor: '#ed4014', borderColor: '#ed4014' }}
                                >
                                  <i className="ri-delete-bin-line text-xs" />
                                  删除
                                </button>
                              )}
                              {resolveCanEdit(row) && (
                                <button
                                  type="button"
                                  onClick={() => openEditUploadModal(row)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-background-200 text-foreground-600 hover:bg-background-50 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                                >
                                  <i className="ri-upload-2-line text-xs" />
                                  修改
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => navigate(`/purchase-plan/${id}`)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-background-200 text-foreground-600 hover:bg-background-50 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                              >
                                <i className="ri-file-list-3-line text-xs" />
                                计划详情
                              </button>
                              <button
                                type="button"
                                disabled={!!exportingId}
                                onClick={() => void handleExportPlan(row)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-background-200 text-foreground-600 hover:bg-background-50 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap disabled:opacity-50"
                              >
                                {exportingId === id ? (
                                  <i className="ri-loader-4-line animate-spin text-xs" />
                                ) : (
                                  <i className="ri-download-2-line text-xs" />
                                )}
                                {exportingId === id ? '导出中...' : '导出'}
                              </button>
                            </div>
                          </td>
                        </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !loadError && (
            <div className="px-4 py-3 border-t border-background-100">
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={total}
                onChange={setPage}
              />
            </div>
          )}
        </div>
      </div>

      {openApprovalId && popupPos && openRow && (
        <div
          ref={(el) => {
            popupRef.current = el;
          }}
          style={{ position: 'fixed', top: popupPos.top, left: popupPos.left }}
          className="z-[100] w-72 bg-background-50 rounded-lg border border-background-200 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 border-b border-background-200 flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground-900">审批流程</span>
            <button
              type="button"
              onClick={() => {
                setOpenApprovalId(null);
                setPopupPos(null);
              }}
              className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-400 hover:text-foreground-600 transition-colors"
            >
              <i className="ri-close-line text-xs" />
            </button>
          </div>
          <div className="px-3 py-2">
            <div className="relative">
              <div className="absolute left-[7px] top-1.5 bottom-2 w-px bg-background-200" />
              {openApprovalNodes.map((node, nidx) => {
                const isLast = nidx === openApprovalNodes.length - 1;
                return (
                  <div key={node.id} className={`relative flex gap-2.5 ${isLast ? '' : 'pb-3'}`}>
                    <div className="relative z-10 shrink-0">
                      <div
                        className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${
                          node.dot === 'done'
                            ? 'bg-accent-500'
                            : node.dot === 'rejected'
                              ? 'bg-primary-500'
                              : node.dot === 'current'
                                ? 'bg-primary-500 ring-2 ring-primary-100'
                                : 'bg-background-100 border border-background-300'
                        }`}
                      >
                        {node.dot === 'done' && (
                          <i className="ri-check-line text-[9px] text-background-50" />
                        )}
                        {node.dot === 'rejected' && (
                          <i className="ri-close-line text-[9px] text-background-50" />
                        )}
                        {node.dot === 'pending' && (
                          <div className="w-1 h-1 rounded-full bg-background-400" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 -mt-px">
                      <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
                        <span
                          className={`text-[11px] leading-relaxed ${
                            node.dot === 'pending'
                              ? 'text-foreground-400'
                              : node.dot === 'rejected'
                                ? 'text-primary-700 font-medium'
                                : 'text-foreground-800 font-medium'
                          }`}
                        >
                          {node.title}
                        </span>
                        {node.subtitle && (
                          <span className="text-[11px] text-foreground-400">{node.subtitle}</span>
                        )}
                        {node.statusText && (
                          <span
                            className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                              node.statusTone === 'done'
                                ? 'text-accent-600'
                                : node.statusTone === 'rejected'
                                  ? 'text-primary-600'
                                  : node.statusTone === 'current'
                                    ? 'text-primary-600'
                                    : 'text-foreground-400'
                            }`}
                          >
                            {node.statusTone === 'current' && (
                              <i className="ri-loader-4-line animate-spin text-[10px]" />
                            )}
                            {node.statusText}
                          </span>
                        )}
                      </div>
                      {node.time && (
                        <span className="text-[11px] text-foreground-400 leading-relaxed block mt-0.5">
                          {formatTime(node.time)}
                        </span>
                      )}
                      {node.comment && (
                        <div className="mt-1.5 px-2 py-1 rounded bg-background-100 text-[11px] text-foreground-600 leading-relaxed">
                          <i className="ri-chat-quote-line text-foreground-400 mr-1" />
                          {node.comment}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {Number(openRow.status) === 2 && openRejectReason && (
            <div className="px-3 py-2 bg-amber-50 border-t border-amber-200">
              <p className="text-[11px] text-amber-700">
                <i className="ri-error-warning-line mr-1" />
                驳回原因：{openRejectReason}
              </p>
            </div>
          )}
        </div>
      )}

      <ApproveModal
        isOpen={modal?.type === 'approve' || modal?.type === 'batch-approve'}
        monthLabel={
          modalRow?.apply_month_text ||
          (modalRow?.apply_month ? String(modalRow.apply_month) : '')
        }
        department={modalRow?.office?.title || ''}
        submitter={modalRow?.member?.nickname || modalRow?.member?.account || ''}
        createdAt={modalRow?.created_at || ''}
        categories={(modalRow?.category_summary || []).map((item) => ({
          name: item.category_name || '未分类',
          planQuantity: Number(item.plan_quantity ?? 0) || 0,
          planAmount: Number(item.plan_amount ?? 0) || 0,
        }))}
        batchCount={modal?.type === 'batch-approve' ? selectedIds.length : undefined}
        batchPlans={
          modal?.type === 'batch-approve'
            ? selectedPlans.map((row) => ({
                monthLabel:
                  row.apply_month_text ||
                  (row.apply_month ? String(row.apply_month) : ''),
                department: row.office?.title || '',
                submitter: row.member?.nickname || row.member?.account || '',
                planAmount: Number(row.plan_amount ?? 0) || 0,
              }))
            : undefined
        }
        submitting={actionSubmitting}
        onCancel={() => {
          if (!actionSubmitting) setModal(null);
        }}
        onApprove={(remark) => {
          if (modal?.type === 'batch-approve') {
            void handleBatchApproveSubmit(true, remark);
            return;
          }
          void handleApproveSubmit(true, remark);
        }}
        onReject={(remark) => {
          if (modal?.type === 'batch-approve') {
            void handleBatchApproveSubmit(false, remark);
            return;
          }
          void handleApproveSubmit(false, remark);
        }}
      />

      <CancelModal
        isOpen={modal?.type === 'cancel'}
        monthLabel={
          modalRow?.apply_month_text ||
          (modalRow?.apply_month ? String(modalRow.apply_month) : '')
        }
        submitting={actionSubmitting}
        onCancel={() => {
          if (!actionSubmitting) setModal(null);
        }}
        onConfirm={handleCancelSubmit}
      />

      <ConfirmModal
        isOpen={modal?.type === 'reverse'}
        title="反审确认"
        message={`确认对 ${modalRow?.apply_month_text || '该'} 采购计划执行反审？反审后将回到审批中，可重新审批。`}
        confirmLabel="确认反审"
        danger
        loading={actionSubmitting}
        onCancel={() => {
          if (!actionSubmitting) setModal(null);
        }}
        onConfirm={() => void handleReverseConfirm()}
      />

      <ConfirmModal
        isOpen={modal?.type === 'delete'}
        title="删除采购计划"
        message={`确认删除 ${modalRow?.apply_month_text || '该'} 采购计划？删除后不可恢复，相关明细将一并清除。`}
        confirmLabel="确认删除"
        danger
        loading={actionSubmitting}
        onCancel={() => {
          if (!actionSubmitting) setModal(null);
        }}
        onConfirm={() => void handleDeleteConfirm()}
      />

      <DownloadTemplateModal
        isOpen={downloadOpen}
        downloading={downloading}
        showDept={showDeptFilter}
        deptOptional={isPurchasePlanManager()}
        deptOptions={deptOptions}
        onCancel={closeDownloadModal}
        onConfirm={(values) => void handleDownloadTemplate(values)}
      />

      {uploadOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-background-50 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <div>
                <h3 className="text-sm font-semibold text-foreground-900">
                  {uploadEditFlowId ? '修改计划表' : '上传计划表'}
                </h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {uploadEditFlowId
                    ? '上传新清单将覆盖当前审批中的采购计划物资'
                    : `仅可提交下月起共 3 个月的计划（${minSubmitMonth} ~ ${maxSubmitMonth}）`}
                </p>
              </div>
              <button
                type="button"
                disabled={submitting}
                onClick={closeUploadModal}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 disabled:opacity-50"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {showDeptFilter && (
                <div>
                  <label
                    className={`text-sm font-medium mb-1.5 block ${
                      uploadErrors.department ? 'text-red-600' : 'text-foreground-800'
                    }`}
                  >
                    采购科室
                  </label>
                  {uploadEditFlowId ? (
                    <div className="px-3 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700">
                      {uploadDeptTitle || '-'}
                    </div>
                  ) : (
                    <SearchableSelect
                      options={deptOptions}
                      value={uploadDeptTitle}
                      onChange={(v) => {
                        setUploadDeptTitle(typeof v === 'string' ? v : v[0] || '');
                        setUploadErrors((prev) => ({ ...prev, department: undefined }));
                      }}
                      placeholder="不选则默认本科室"
                      allLabel="不选则默认本科室"
                      icon="ri-hospital-line"
                      searchPlaceholder="搜索科室..."
                      invalid={!!uploadErrors.department}
                    />
                  )}
                  {uploadErrors.department && (
                    <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1 font-medium">
                      <i className="ri-error-warning-line" />
                      {uploadErrors.department}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label
                  className={`text-sm font-medium mb-1.5 block ${
                    uploadErrors.month ? 'text-red-600' : 'text-foreground-800'
                  }`}
                >
                  <span className="text-red-500 mr-0.5">*</span>
                  计划月份
                </label>
                <MonthPicker
                  value={uploadMonth}
                  onChange={(v) => {
                    if (uploadEditFlowId) return;
                    setUploadMonth(v);
                    setUploadErrors((prev) => ({ ...prev, month: undefined }));
                  }}
                  minMonth={uploadEditFlowId ? uploadMonth : minSubmitMonth}
                  maxMonth={uploadEditFlowId ? uploadMonth : maxSubmitMonth}
                  placeholder="请选择计划月份"
                  invalid={!!uploadErrors.month}
                  disabled={!!uploadEditFlowId || submitting}
                />
                {uploadErrors.month && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1 font-medium">
                    <i className="ri-error-warning-line" />
                    {uploadErrors.month}
                  </p>
                )}
              </div>

              <div>
                <label
                  className={`text-sm font-medium mb-1.5 block ${
                    uploadErrors.file ? 'text-red-600' : 'text-foreground-800'
                  }`}
                >
                  <span className="text-red-500 mr-0.5">*</span>
                  采购清单模板
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setUploadFile(file);
                    setUploadErrors((prev) => ({ ...prev, file: undefined }));
                  }}
                />
                <div
                  className={`rounded-lg border border-dashed px-4 py-4 transition-colors ${
                    uploadErrors.file
                      ? 'border-red-500 ring-1 ring-red-200 bg-red-50'
                      : 'border-background-300 bg-background-50'
                  }`}
                >
                  {uploadFile ? (
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-foreground-800 truncate">{uploadFile.name}</p>
                        <p className="text-xs text-foreground-400 mt-0.5">
                          {(uploadFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={submitting}
                        onClick={() => {
                          setUploadFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="text-xs text-foreground-500 hover:text-red-600"
                      >
                        移除
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full text-center py-2"
                    >
                      <i
                        className={`ri-upload-cloud-2-line text-2xl ${
                          uploadErrors.file ? 'text-red-400' : 'text-foreground-300'
                        }`}
                      />
                      <p
                        className={`text-sm mt-1 ${
                          uploadErrors.file ? 'text-red-600' : 'text-foreground-600'
                        }`}
                      >
                        点击上传采购清单模板
                      </p>
                      <p
                        className={`text-xs mt-0.5 ${
                          uploadErrors.file ? 'text-red-500' : 'text-foreground-400'
                        }`}
                      >
                        支持 xlsx / xls / csv
                      </p>
                    </button>
                  )}
                </div>
                {uploadErrors.file && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1 font-medium">
                    <i className="ri-error-warning-line" />
                    {uploadErrors.file}
                  </p>
                )}
                {uploadFile && (
                  <button
                    type="button"
                    className="mt-2 text-xs text-primary-600 hover:text-primary-700"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    重新选择文件
                  </button>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-foreground-800 mb-1.5 block">
                  备注（选填）
                </label>
                <textarea
                  value={uploadRemark}
                  disabled={submitting}
                  onChange={(e) => setUploadRemark(e.target.value.slice(0, 1000))}
                  rows={3}
                  maxLength={1000}
                  placeholder="可填写计划说明"
                  className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm outline-none resize-none focus:border-primary-400 disabled:bg-background-100"
                />
              </div>
            </div>

            <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={submitting}
                onClick={closeUploadModal}
                className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => void handleSubmitUpload()}
                className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {submitting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />}
                {submitting
                  ? '提交中...'
                  : uploadEditFlowId
                    ? '确认覆盖'
                    : '提交计划'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
